// api/candleCache.ts — per-SYMBOL client candle cache (F1b, design §4.4).
//
// Ported from the OLD chart's intervalCache.ts (getCached/setCached), but keyed
// by SYMBOL ONLY — the modular store is 1m-base-only and derives every higher TF
// client-side via aggregateCandles, so there is NO per-interval cache. This is a
// pure within-session speedup: re-selecting a symbol the user already loaded skips
// the network round-trip in ensureLoaded (the WS ohlc-snapshot still owns first
// paint of a never-seen symbol; this only short-circuits re-selection).
//
// What's cached is the RESOLVED 1m base series + trade flow + the seedDepth (the
// 1m base-candle count requiredBaseCandles produced for the load). seedDepth lets
// the re-deepen path (ensureLoaded redeepen) decide whether the cached series is
// deep enough for a newly-mounting higher-TF panel, or whether a deeper fetch is
// still required.
//
// NOT store state — this is process-lifetime side-effect bookkeeping (same pattern
// as panelDataStore.lifecycle.ts / .older.ts). It is intentionally never cleared
// on teardown: surviving a refcount 1→0→1 cycle is the whole point.

import type { CandleData } from '../stores/priceDataStore';
import type { TradeFlowPoint } from '../stores/indicatorStore';

export interface CachedSymbolData {
  /** 1m base series (sorted ascending, deduped) at the moment it was cached. */
  candles: CandleData[];
  /** Trade flow that accompanied the load (best-effort; may be empty). */
  flow: TradeFlowPoint[];
  /** The requiredBaseCandles depth used for this load (1m base-bar count). */
  seedDepth: number;
}

const cache = new Map<string, CachedSymbolData>();

/** Read the cached 1m base series for a symbol, or null on a miss. */
export function getCachedSymbol(symbol: string): CachedSymbolData | null {
  return cache.get(symbol) ?? null;
}

/**
 * Cache the resolved 1m base series for a symbol. Called when a network load (or
 * a deeper re-fetch) resolves. A later write with a SHALLOWER seedDepth than what
 * is already cached is dropped so the cache only ever deepens — re-selecting a
 * symbol then re-shrinking the depth must never evict the deep series a high-TF
 * panel warmed.
 */
export function setCachedSymbol(symbol: string, data: CachedSymbolData): void {
  const prior = cache.get(symbol);
  if (prior !== undefined && prior.seedDepth >= data.seedDepth) {
    // Keep the deeper cached series; only refresh its candles/flow in place so
    // newer live-merged bars aren't lost when the shallower load resolves later.
    cache.set(symbol, {
      candles: data.candles,
      flow: data.flow,
      seedDepth: prior.seedDepth,
    });
    return;
  }
  cache.set(symbol, data);
}

/** Test/diagnostic hook: number of symbols currently cached. */
export function cachedSymbolCount(): number {
  return cache.size;
}

/** Drop a symbol from the cache (not wired into teardown by design; exposed for
 *  tests and an explicit session reset). */
export function evictCachedSymbol(symbol: string): void {
  cache.delete(symbol);
}
